/**
 * Compatibility shim: `@material-ui/pickers` v3 surface on @mui/x-date-pickers v8.
 *
 * Maps the three components the ported tree uses (MuiPickersUtilsProvider,
 * DatePicker, KeyboardDatePicker) onto the v8 LocalizationProvider + DatePicker.
 * Prop translation is best-effort — verify date fields render and emit Date values.
 */
import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as XDatePicker } from '@mui/x-date-pickers/DatePicker';

/** v3 MuiPickersUtilsProvider -> v8 LocalizationProvider (the `utils` prop is ignored). */
export function MuiPickersUtilsProvider({
  children,
}: {
  utils?: unknown;
  locale?: unknown;
  children?: React.ReactNode;
}) {
  return <LocalizationProvider dateAdapter={AdapterDateFns}>{children}</LocalizationProvider>;
}

function mapPickerProps(props: any) {
  const {
    value,
    onChange,
    format,
    label,
    disabled,
    minDate,
    maxDate,
    disablePast,
    disableFuture,
    readOnly,
    views,
    openTo,
    // -> forwarded to the underlying text field
    inputVariant,
    fullWidth,
    margin,
    size,
    error,
    helperText,
    name,
    placeholder,
    className,
    required,
    inputProps,
    // v3-only props with no v8 equivalent: drop them (do NOT spread the rest onto
    // the v8 DatePicker, or they leak to the DOM / show the format as a placeholder).
    /* eslint-disable @typescript-eslint/no-unused-vars */
    autoOk,
    variant,
    KeyboardButtonProps,
    disableToolbar,
    InputAdornmentProps,
    KeyboardButtonPropsProps,
    /* eslint-enable @typescript-eslint/no-unused-vars */
    ..._ignored
  } = props;

  return {
    value: value ?? null,
    onChange,
    format,
    label,
    disabled,
    minDate,
    maxDate,
    disablePast,
    disableFuture,
    readOnly,
    views,
    openTo,
    slotProps: {
      textField: {
        variant: inputVariant ?? 'standard',
        fullWidth,
        margin,
        // v4 pickers were compact via `margin="dense"`; in v5+ height compaction
        // moved to `size="small"`. Default to small (matching the app's dense v4
        // look and ResponsiveSelect) so migrated pickers aren't full-height; an
        // explicit `size` still wins.
        size: size ?? 'small',
        error,
        helperText,
        name,
        placeholder,
        className,
        required,
        inputProps,
      },
    },
  };
}

export function DatePicker(props: any) {
  return <XDatePicker {...mapPickerProps(props)} />;
}

/** v8 has no separate keyboard variant — DatePicker accepts typed input by default. */
export function KeyboardDatePicker(props: any) {
  return <XDatePicker {...mapPickerProps(props)} />;
}
